import React, { useEffect } from 'react'
import { TrackingContextCard } from 'components/TrackingContextCard/TrackingContextCard'
import moment from 'moment-timezone'
import { POSITION_TYPE, IMapStopProps, WithId } from 'transfix-maps/types'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { IUserProfileDetails, IDriverLocation, IRoute, INextStop } from 'types/Location'
import { IGeocoding } from 'types/graphqlTypes'
import { IShipperTrackingSegment, ITrackingStatus, STOP_TYPE } from 'types/shipment'
import { getGeocodingCityState } from 'utils/getGeocodingCityState'
import { FORMATS } from 'constants/datetime'
import { PATH_LOCATIONS_LIMIT } from 'constants/locations'
import { LastPingMap } from './components/LastPingMap'

export interface ILastPingProps {
  getGeoCode: Function
  getDriverLocations: Function
  getParentDriverLocations: Function
  userProfileDetails: IUserProfileDetails
  driverLocations: IDriverLocation[]
  fetching: boolean
  features: Partial<IGeocoding>[]
  milesToGo?: number
  getPath?: Function
  path?: IDriverLocation[]
  shipmentId?: string
  shipmentUuid?: string
  stops: INextStop[]
  previousRoute?: IRoute[]
  predictedRoute?: IRoute[]
  trackingRequired?: boolean
  trackingStatus?: ITrackingStatus
  shipperTrackingSegment?: IShipperTrackingSegment
}

export function LastPing({
  getGeoCode,
  getDriverLocations,
  getParentDriverLocations,
  userProfileDetails,
  driverLocations,
  fetching,
  features,
  milesToGo,
  // getPath, path and shipmentUuid are needed only when displaying driver path on the map
  getPath,
  path,
  shipmentId,
  shipmentUuid,
  stops = [],
  predictedRoute = [],
  previousRoute = [],
  trackingStatus,
  shipperTrackingSegment,
}: ILastPingProps) {
  // We always want to exclude manual pings from tracking icon
  const driverLocationsToUseForTracking =
    driverLocations && driverLocations.length
      ? driverLocations.filter(l => l.sourceType !== 'Manual')
      : []
  const [lastLocation] = driverLocationsToUseForTracking || []
  const { phoneTimestamp, shipmentId: lastLocationShipmentId } = lastLocation || {}
  const [lastLocationForLastPing] = driverLocations || userProfileDetails.driverLocations || []
  const { latitude, longitude, phoneTimestamp: phoneTimestampForPing, sourceType } =
    lastLocationForLastPing || {}

  const mapShipmentId = shipmentId || lastLocationShipmentId
  const userUuid = userProfileDetails ? userProfileDetails.uuid : undefined
  const parentUserId = userProfileDetails ? userProfileDetails.id : undefined
  const previousLocations = (driverLocations || [])
    .filter(l => l.phoneTimestamp !== phoneTimestamp)
    .slice(1, 3)
  const locationString = latitude && longitude ? `lat/lng ${latitude}/${longitude}` : 'Not Tracking'
  const placeName = getGeocodingCityState(features)

  const pickupsAndDeliveries: WithId<IMapStopProps>[] = []
  if (stops.length) {
    stops.forEach((stop, id) => {
      const { latitude, longitude, stopSequence, stopType } = stop

      pickupsAndDeliveries.push({
        type: stopType === 'pickup' ? STOP_TYPE.pickup : STOP_TYPE.delivery,
        coords: {
          lat: parseFloat(latitude),
          lon: parseFloat(longitude),
        },
        sequence: stopSequence,
        data: {
          id,
        },
      })
    })
  }

  const predictedMapBoxRoute = predictedRoute[0]
  const { geometry: { coordinates: predictedRouteCoordinates = [] } = {} } =
    predictedMapBoxRoute || []
  const previousMapBoxRoute = previousRoute[0]
  const { geometry: { coordinates: previousRouteCoordinates = [] } = {} } =
    previousMapBoxRoute || []

  const previousRouteCoords = previousRouteCoordinates.map(([lon, lat]) => ({
    coords: {
      lat,
      lon,
    },
  }))
  const predictedRouteCoords = predictedRouteCoordinates.map(([lon, lat]) => ({
    coords: {
      lat,
      lon,
    },
  }))

  useEffect(() => {
    if (latitude && longitude) {
      getGeoCode({ lat: latitude, lng: longitude })
      if (!previousLocations.length) {
        if (parentUserId) getParentDriverLocations(parentUserId, 3, false, true)
        getDriverLocations(userUuid, 3, false, true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, parentUserId, userUuid])

  useEffect(() => {
    if (getPath && shipmentUuid) {
      getPath(shipmentUuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentUuid])

  const locations =
    getPath && shipmentUuid && path ? path.slice(0, PATH_LOCATIONS_LIMIT) : previousLocations

  const positions = [
    ...locations.map((point, id) => ({
      type: POSITION_TYPE.PING,
      coords: {
        lat: parseFloat(point.latitude),
        lon: parseFloat(point.longitude),
      },
      data: {
        id,
      },
    })),
  ]

  if (latitude && longitude) {
    positions.push({
      type: POSITION_TYPE.LAST_PING,
      coords: {
        lat: parseFloat(latitude),
        lon: parseFloat(longitude),
      },
      data: {
        id: positions.length + 1,
      },
    })
  }

  const mileageDisplayText = milesToGo ? `Distance to destination ${milesToGo} mi` : ''

  return fetching ? (
    <Spinner />
  ) : (
    <>
      <div className='flex items-center mb2'>
        <TrackingContextCard
          shipmentId={Number(mapShipmentId)}
          trackingStatus={trackingStatus}
          shipperTrackingSegment={shipperTrackingSegment}
        />
        <p>
          <strong>{placeName || locationString}</strong>
          {mapShipmentId && (
            <>
              <span> on </span>
              <a
                href={`${__ROOT__}/shipment-details/${mapShipmentId}?tab=scheduling`}
                target='_blank'
                rel='noopener noreferrer'>
                Shipment #{mapShipmentId}
              </a>
            </>
          )}
        </p>
      </div>
      {phoneTimestampForPing && (
        <p>
          Last ping{' '}
          {moment
            .tz(phoneTimestampForPing, FORMATS.defaultTimeZone)
            .format(FORMATS.driverLastLocation)}
          &nbsp;({sourceType})
        </p>
      )}
      <p>{mileageDisplayText}</p>
      {sourceType === 'Manual' && (
        <InlineMessage message='Manual' type={INLINE_MESSAGE_VARIANTS.ALERT} className='mb2' />
      )}
      <LastPingMap
        previousRouteCoords={previousRouteCoords}
        predictedRouteCoords={predictedRouteCoords}
        positions={positions}
        pickupsAndDeliveries={pickupsAndDeliveries}
      />
    </>
  )
}
